<template>
  <div class="page-container">
    <header class="header">
      <el-button
        text
        class="back-btn"
        aria-label="返回"
        @click="handleBack"
      >
        <img src="@/assets/返回.svg" alt="返回" class="icon-back" />
      </el-button>
      <h1 class="title">红冲</h1>
      <div class="header-right">
        <div class="header-placeholder"></div>
      </div>
    </header>

    <main
      class="content"
      v-loading="loading"
      element-loading-text="加载中..."
    >
      <template v-if="!loading && detailData">
        <el-card shadow="never" class="info-card">
          <el-descriptions :column="1">
            <el-descriptions-item
              :label="detailData.receiptNo ? 'PLS单号' : '入库单号'"
            >
              {{ detailData.receiptNo || detailData.wareInNo || "-" }}
            </el-descriptions-item>
            <el-descriptions-item label="创建时间">
              {{ formatTime(detailData.createTime) }}
            </el-descriptions-item>
            <el-descriptions-item label="供应商">
              {{ detailData.supplierName || "-" }}
            </el-descriptions-item>
          </el-descriptions>
        </el-card>

        <el-card shadow="never" class="redash-card">
          <template #header>
            <div class="card-title">红冲信息</div>
          </template>

          <div class="material-list">
            <article
              v-for="detail in detailData.wareInDetailList"
              :key="detail.id"
              class="material-card"
            >
              <div class="material-head">
                <div class="material-code">{{ detail.materialNo || "-" }}</div>
                <el-tag
                  size="small"
                  :type="detail.isHc === 1 ? 'danger' : 'info'"
                  effect="light"
                >
                  {{ detail.isHc === 1 ? "已红冲" : "未红冲" }}
                </el-tag>
              </div>
              <div class="material-meta">
                <span class="material-desc">{{
                  detail.materialDesc || "-"
                }}</span>
                <span class="material-qty"
                  >入库 {{ formatQty(detail.wareInNum) }}</span
                >
              </div>

              <div v-if="detail.recordVOList?.length" class="pallet-list">
                <div
                  v-for="record in detail.recordVOList"
                  :key="record.id"
                  class="pallet-row"
                >
                  <div class="pallet-line">
                    <span class="pallet-code">{{
                      record.palletCode || "-"
                    }}</span>
                    <span class="pallet-qty">{{
                      formatQty(record.wareInNum)
                    }}</span>
                  </div>
                  <div class="qty-field">
                    <span>红冲数量</span>
                    <el-input
                      v-model.number="
                        redashQuantities[
                          getRecordKey(detail.materialId, record.id)
                        ]
                      "
                      type="number"
                      size="small"
                      class="redash-input"
                      placeholder="负数红冲"
                      inputmode="decimal"
                      @input="handleQuantityInput"
                    />
                  </div>
                </div>
              </div>
              <div v-else class="pallet-row">
                <div class="qty-field">
                  <span>红冲数量</span>
                  <el-input
                    v-model.number="redashQuantities[detail.materialId]"
                    type="number"
                    size="small"
                    class="redash-input"
                    placeholder="负数红冲"
                    inputmode="decimal"
                    @input="handleQuantityInput"
                  />
                </div>
              </div>
            </article>
          </div>
        </el-card>
      </template>
    </main>

    <footer class="footer" v-if="detailData">
      <el-button
        type="danger"
        class="confirm-btn"
        :disabled="!hasValidRedash"
        :loading="submitting"
        @click="handleSubmit"
      >
        {{ submitting ? "提交中..." : "提交红冲" }}
      </el-button>
    </footer>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { ElMessage, ElMessageBox } from "element-plus";
import { getWareInReceiveDetail, redFlushByWare } from "@/api/wareInReceive";

const route = useRoute();
const router = useRouter();

const loading = ref(false);
const submitting = ref(false);
const detailData = ref(null);
const redashQuantities = ref({});

const hasValidRedash = computed(() => {
  const quantities = redashQuantities.value;
  return Object.values(quantities).some(
    (val) => val !== null && val !== undefined && val !== "" && val !== 0,
  );
});

const getRecordKey = (materialId, recordId) => {
  return `${materialId}_${recordId}`;
};

const formatTime = (val) => {
  if (!val) return "-";
  return String(val);
};

const formatQty = (val) => {
  if (val === null || val === undefined || val === "") return "-";
  const n = Number(val);
  if (Number.isNaN(n)) return String(val);
  return n.toFixed(3);
};

const loadDetail = async () => {
  const wareInId = route.query?.wareInId;
  if (!wareInId) {
    ElMessage.error("缺少入库单ID");
    return;
  }

  loading.value = true;
  try {
    const res = await getWareInReceiveDetail(wareInId);
    detailData.value = res?.data;
  } catch (err) {
    console.error("加载详情失败", err);
    ElMessage.error("加载失败");
  } finally {
    loading.value = false;
  }
};

const handleQuantityInput = () => {
  console.log("红冲数量更新:", redashQuantities.value);
};

const isFilledQty = (qty) =>
  qty !== null && qty !== undefined && qty !== "" && qty !== 0;

const collectValidRecords = () => {
  const quantities = redashQuantities.value;
  const details = detailData.value?.wareInDetailList || [];
  const validRecords = [];

  for (const detail of details) {
    if (detail.recordVOList?.length) {
      for (const record of detail.recordVOList) {
        const key = getRecordKey(detail.materialId, record.id);
        const qty = quantities[key];
        if (!isFilledQty(qty)) continue;
        validRecords.push({
          materialId: String(detail.materialId || record.materialId),
          palletId: String(record.palletId || ""),
          wareId: String(record.wareId || ""),
          wareInNum: Number(qty),
        });
      }
    } else {
      const qty = quantities[detail.materialId];
      if (!isFilledQty(qty)) continue;
      validRecords.push({
        materialId: String(detail.materialId),
        palletId: "",
        wareId: "",
        wareInNum: Number(qty),
      });
    }
  }

  return validRecords;
};

const handleSubmit = async () => {
  if (!hasValidRedash.value) {
    ElMessage.warning("请输入红冲数量");
    return;
  }

  const validRecords = collectValidRecords();
  if (validRecords.length === 0) {
    ElMessage.warning("没有有效的红冲数据");
    return;
  }

  const hasPositive = validRecords.some((r) => r.wareInNum > 0);
  const hasNegative = validRecords.some((r) => r.wareInNum < 0);

  let confirmMessage = "确定要进行红冲出库操作吗？";
  if (hasPositive && hasNegative) {
    confirmMessage = "同时存在补入库和红冲出库操作，确定要继续吗？";
  } else if (hasPositive) {
    confirmMessage = "确定要进行补入库操作吗？";
  }

  try {
    await ElMessageBox.confirm(confirmMessage, "确认", {
      confirmButtonText: "确定",
      cancelButtonText: "取消",
      type: "warning",
      center: true,
      customClass: "redash-confirm-box",
      confirmButtonClass: "el-button--danger redash-confirm-ok",
      cancelButtonClass: "redash-confirm-cancel",
    });
    await doSubmit();
  } catch {
    // 用户取消
  }
};

const doSubmit = async () => {
  const wareInId = route.query?.wareInId;
  if (!wareInId) {
    ElMessage.error("缺少入库单ID");
    return;
  }

  const validRecords = collectValidRecords();
  if (validRecords.length === 0) {
    ElMessage.warning("没有有效的红冲数据");
    return;
  }

  const type = validRecords.every((item) => item.wareInNum > 0) ? 0 : 1;
  const params = {
    wareInId: String(wareInId),
    type,
    remark: "",
    wareInDetailRecordVOList: validRecords,
  };

  submitting.value = true;
  try {
    console.log("红冲提交参数:", params);
    const res = await redFlushByWare(params);

    if (res?.success) {
      ElMessage.success("红冲提交成功");
      setTimeout(() => {
        router.back();
      }, 1500);
    } else {
      ElMessage.error(res?.message || res?.msg || "红冲提交失败");
    }
  } catch (err) {
    console.error("红冲提交失败", err);
    ElMessage.error("红冲提交失败");
  } finally {
    submitting.value = false;
  }
};

const handleBack = () => {
  router.back();
};

onMounted(() => {
  loadDetail();
});
</script>

<style scoped>
.page-container {
  min-height: 100vh;
  background: var(--background-color);
  padding-bottom: 100px;
}

.page-container,
.page-container * {
  box-sizing: border-box;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  padding-top: calc(12px + env(safe-area-inset-top));
  background-color: var(--header-background);
  color: var(--header-text);
  box-shadow: 0 2px 8px rgba(35, 177, 192, 0.18);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: calc(64px + env(safe-area-inset-top));
}

.back-btn {
  width: 40px;
  height: 40px;
  padding: 0;
  margin: 0;
}

.back-btn :deep(.el-button) {
  padding: 0;
}

.icon-back {
  width: 22px;
  height: 22px;
}

.title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.header-right {
  width: 36px;
}

.header-placeholder {
  width: 100%;
}

.content {
  padding: 10px 12px;
  margin-top: calc(64px + env(safe-area-inset-top));
  min-height: 200px;
}

.info-card,
.redash-card {
  margin-bottom: 12px;
  border: none;
  border-radius: 8px;
}

.info-card :deep(.el-card__body),
.redash-card :deep(.el-card__body) {
  padding: 12px;
}

.redash-card :deep(.el-card__header) {
  padding: 10px 12px;
  border-bottom: 1px solid #f0f0f0;
}

.card-title {
  font-size: 15px;
  font-weight: 600;
  color: #333;
  text-align: center;
}

.info-card :deep(.el-descriptions__label) {
  color: #999;
  width: 72px;
  font-size: 14px;
}

.info-card :deep(.el-descriptions__content) {
  color: #333;
  font-weight: 500;
  font-size: 14px;
}

.info-card :deep(.el-descriptions__cell) {
  padding-bottom: 8px;
}

.material-list {
  display: flex;
  flex-direction: column;
}

.material-card {
  padding: 10px 0 12px;
  border-top: 1px solid #f2f2f2;
}

.material-card:first-child {
  border-top: none;
  padding-top: 0;
}

.material-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.material-code {
  min-width: 0;
  font-size: 15px;
  font-weight: 600;
  color: #222;
  line-height: 1.3;
  word-break: break-all;
}

.material-meta {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  margin-top: 2px;
}

.material-desc {
  min-width: 0;
  color: #999;
  font-size: 12px;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.material-qty {
  flex-shrink: 0;
  color: #666;
  font-size: 12px;
}

.pallet-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 8px;
}

.pallet-row {
  padding: 8px 10px;
  background: #f7f8fa;
  border-radius: 6px;
}

.pallet-line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.pallet-code {
  min-width: 0;
  color: #222;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
  word-break: break-all;
}

.pallet-qty {
  flex-shrink: 0;
  color: #333;
  font-size: 14px;
  font-weight: 600;
}

.qty-field {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 6px;
}

.qty-field > span {
  flex-shrink: 0;
  color: #666;
  font-size: 13px;
}

.redash-input {
  width: 108px;
}

.redash-input :deep(.el-input__wrapper) {
  padding: 0 8px;
}

.redash-input :deep(.el-input__inner) {
  text-align: right;
  height: 28px;
  line-height: 28px;
}

.footer {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 12px;
  background: #fff;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.04);
}

.confirm-btn {
  width: 100%;
  height: 48px;
  font-size: 17px;
  font-weight: 600;
}

.confirm-btn :deep(span) {
  font-size: 17px;
}
</style>

<style>
/* MessageBox 挂载到 body，需非 scoped */
.redash-confirm-box {
  width: min(86vw, 320px) !important;
  padding-bottom: 8px;
}

.redash-confirm-box .el-message-box__title {
  font-size: 18px;
}

.redash-confirm-box .el-message-box__message {
  font-size: 16px;
  line-height: 1.6;
  padding: 8px 0 4px;
}

.redash-confirm-box .el-message-box__btns {
  display: flex;
  gap: 12px;
  padding: 12px 16px 16px;
}

.redash-confirm-box .redash-confirm-cancel,
.redash-confirm-box .redash-confirm-ok {
  flex: 1;
  margin: 0 !important;
  height: 44px;
  font-size: 16px;
  border-radius: 8px;
}
</style>
